import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  TextField,
  MenuItem,
  Paper,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Chip,
  CircularProgress,
  Typography,
  Autocomplete,
  Button,
} from '@mui/material';
import {
  BarChart,
  ViewCompact,
  PeopleAlt,
  Groups,
  Refresh,
  FilterAlt,
  AutoFixHigh,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import StyledButton from 'components/StyledButton';
import NumberControl from 'components/NumberControl';
import CustomDatePicker from 'components/CustomDatePicker';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import ScrollToTop from 'components/ScrollToTop';
import QualificationMatrix from './QualificationMatrix';
import ExpiryHeatmap from './ExpiryHeatmap';
import EmployeeNeedTraining from './EmployeeNeedTraining';
import ClassBuilder from './ClassBuilder';
import MatrixFilterDialog from './MatrixFilterDialog';


const INITIAL_FILTERS = { ranks: [], bases: [], employeeCode: '', employeeName: '' };

const TrainingPlanner = () => {
  useAuthCheck();

  const scrollRef = useRef(null);

  const [tab, setTab] = useState(0);
  const [lookAheadMonths, setLookAheadMonths] = useState(3);
  const [subjects, setSubjects]           = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedRank, setSelectedRank]   = useState('');
  const [accumulatedEmployees, setAccumulatedEmployees] = useState([]);

  const [matrixData, setMatrixData]       = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [filters, setFilters]             = useState(INITIAL_FILTERS);
  const [filterOpen, setFilterOpen]       = useState(false);

  const activeFilterCount =
    filters.ranks.length +
    filters.bases.length +
    (filters.employeeCode ? 1 : 0) +
    (filters.employeeName ? 1 : 0);

  const removeFilter = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: prev[field].filter((v) => v !== value) }));

  const fetchMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const params = {};
      if (filters.ranks?.length)   params.ranks = filters.ranks;
      if (filters.bases?.length)   params.bases = filters.bases;
      if (filters.employeeCode)    params.employeeCode = filters.employeeCode;
      if (filters.employeeName)    params.employeeName = filters.employeeName;
      const res = await api.get('/qualifications/matrix', { params });
      setMatrixData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load qualification matrix.');
      setMatrixData(null);
    } finally {
      setMatrixLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  useEffect(() => {
    api.get('/trainingsubjects/active')
      .then((res) => setSubjects(res.data))
      .catch(() => toast.error('Failed to load subjects!'));
  }, []);

  const selectedSubject = useMemo(
    () => subjects.find((s) => s._id === selectedSubjectId),
    [subjects, selectedSubjectId]
  );

  const availableRanks = useMemo(() => {
    if (!matrixData?.employees) return [];
    const RANK_PRIORITY = ['CP', 'FO', 'SE', 'PU', 'CM', 'BA', 'SA'];
    const rankOrder = (r) => { const i = RANK_PRIORITY.indexOf(r); return i === -1 ? 999 : i; };
    return [...new Set(matrixData.employees.map((e) => e.rank).filter(Boolean))]
      .sort((a, b) => rankOrder(a) - rankOrder(b));
  }, [matrixData]);

  const handleHeatmapCellClick = ({ subject, rank }) => {
    setSelectedSubjectId(subject._id);
    setSelectedRank(rank || '');
    setTab(2);
  };

  const handleAddToBuilder = (employeeList) => {
    setAccumulatedEmployees((prev) => {
      const existingCodes = new Set(prev.map((c) => c.employeeCode));
      const newEmployees = employeeList.filter((c) => !existingCodes.has(c.employeeCode));
      return [...prev, ...newEmployees];
    });
    setTab(3);
  };

  const handleRemoveEmployee = (employeeCode) =>
    setAccumulatedEmployees((prev) => prev.filter((c) => c.employeeCode !== employeeCode));

  // Class Builder state
  const [maxClassSize, setMaxClassSize]     = useState(20);
  const [targetDate, setTargetDate]         = useState(null);
  const [selectedRanks, setSelectedRanks]   = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestedGroups, setSuggestedGroups] = useState(null);

  const handleSuggest = async () => {
    if (!selectedSubjectId) { toast.error('Please select a subject.'); return; }
    if (!targetDate)        { toast.error('Please select a target date.'); return; }
    try {
      setSuggestLoading(true);
      const res = await api.post('/qualifications/suggest', {
        subjectId: selectedSubjectId,
        targetDate: format(targetDate, 'yyyy-MM-dd'),
        maxClassSize: Number(maxClassSize),
        ranks: selectedRanks,
      });
      setSuggestedGroups(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to get suggestions.');
    } finally {
      setSuggestLoading(false);
    }
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Training Planner"
          subtitle="Qualification Matrix"
        />
        <Box
          display="flex"
          alignItems="center"
          gap={1}
        >
          <ToggleButtonGroup
            value={tab}
            exclusive
            onChange={(_, v) => v !== null && setTab(v)}
            size="small"
          >
            <Tooltip title="Qualification Matrix"><ToggleButton value={0}><ViewCompact /></ToggleButton></Tooltip>
            <Tooltip title="Expiry Heatmap"><ToggleButton value={1}><BarChart /></ToggleButton></Tooltip>
            <Tooltip title="Employee Needing Training"><ToggleButton value={2}><PeopleAlt /></ToggleButton></Tooltip>
            <Tooltip title={`Class Builder${accumulatedEmployees.length ? ` (${accumulatedEmployees.length})` : ''}`}>
              <ToggleButton value={3}><Groups /></ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          <Tooltip title="Filter" arrow>
            <StyledButton onClick={() => setFilterOpen(true)}>
              <Badge badgeContent={activeFilterCount} color="primary" max={9}>
                <FilterAlt />
              </Badge>
            </StyledButton>
          </Tooltip>

          <Tooltip title="Refresh" arrow>
            <span>
              <StyledButton onClick={fetchMatrix} disabled={matrixLoading}>
                {matrixLoading
                  ? <CircularProgress size={21} color="inherit" />
                  : <Refresh />}
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </FlexBetween>
      
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          ref={scrollRef} 
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
          {/* Selection panel */}
          {(tab === 1 || tab === 2 || tab === 3) && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.5,
                  mb: 1.5,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: { sm: 'wrap' },
                  gap: 1.5,
                  alignItems: { xs: 'stretch', sm: 'center' },
                }}
              >
                {(tab === 2 || tab === 3) && (
                  <TextField
                    select size="small" label="Subject"
                    value={selectedSubjectId}
                    onChange={(e) => { 
                      setSelectedSubjectId(e.target.value); 
                      setSelectedRank(''); 
                      setSuggestedGroups(null); 
                    }}
                    sx={{ width: { xs: '100%', sm: 333 } }}
                  >
                    <MenuItem value="">- All subjects -</MenuItem>
                    {subjects.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.subjectCode} - {s.subjectName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {(tab === 1 || tab === 2) && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Look ahead
                    </Typography>
                    <NumberControl
                      value={lookAheadMonths}
                      onChange={setLookAheadMonths}
                      min={1}
                      max={36}
                      textFieldWidth={48}
                    />
                    <Typography variant="caption" color="text.secondary">
                      months
                    </Typography>
                  </Box>
                )}

                {tab === 3 && (
                  <>
                    <CustomDatePicker
                      label="Target End Date"
                      name="targetDate"
                      value={targetDate}
                      onChange={(_, val) => setTargetDate(val)}
                      size="small"
                      variant="outlined"
                      sx={{ width: { xs: '100%', sm: 160 } }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Max Class Size"
                      value={maxClassSize}
                      onChange={(e) => setMaxClassSize(e.target.value)}
                      slotProps={{ inputProps: { min: 1, max: 100 } }}
                      sx={{ width: { xs: '100%', sm: 120 } }}
                    />
                    {availableRanks.length > 0 && (
                      <Autocomplete
                        multiple
                        size="small"
                        options={availableRanks}
                        value={selectedRanks}
                        onChange={(_, v) => setSelectedRanks(v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Ranks"
                            placeholder={selectedRanks.length === 0 ? 'All' : ''}
                          />
                        )}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 120 } }}
                      />
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={suggestLoading ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
                      onClick={handleSuggest}
                      disabled={suggestLoading}
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    >
                      Suggest Groups
                    </Button>
                  </>
                )}
              </Paper>
            </LocalizationProvider>
          )}

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
              {filters.ranks.map((r) => (
                <Chip 
                  key={r} 
                  label={`Rank: ${r}`} 
                  size="small" 
                  onDelete={() => removeFilter('ranks', r)} 
                />
              ))}
              {filters.bases.map((b) => (
                <Chip 
                  key={b} label={`Base: ${b}`} 
                  size="small" 
                  onDelete={() => removeFilter('bases', b)} 
                />
              ))}
              {filters.employeeCode && (
                <Chip
                  label={`Code: ${filters.employeeCode}`} 
                  size="small"
                  onDelete={() => setFilters((f) => ({ ...f, employeeCode: '' }))}
                />
              )}
              {filters.employeeName && (
                <Chip
                  label={`Name: ${filters.employeeName}`} 
                  size="small"
                  onDelete={() => setFilters((f) => ({ ...f, employeeName: '' }))}
                />
              )}
              <Chip
                label="Clear all" 
                size="small" 
                variant="outlined"
                onClick={() => setFilters(INITIAL_FILTERS)}
              />
            </Box>
          )}

          {/* Tab 0: Qualification Matrix */}
          {tab === 0 && (
            <Paper elevation={0} sx={{ overflowX: 'auto' }}>
              {matrixLoading ? (
                <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
              ) : (
                <QualificationMatrix matrixData={matrixData} />
              )}
            </Paper>
          )}

          {/* Tab 1: Expiry Heatmap */}
          {tab === 1 && (
            <Paper elevation={0} sx={{ overflowX: 'auto' }}>
              <ExpiryHeatmap
                matrixData={matrixData}
                lookAheadMonths={lookAheadMonths}
                onCellClick={handleHeatmapCellClick}
              />
            </Paper>
          )}

          {/* Tab 2: Employee Needing Training */}
          {tab === 2 && (
            <Paper elevation={0} sx={{ overflowX: 'auto' }}>
              <EmployeeNeedTraining
                matrixData={matrixData}
                subjectId={selectedSubjectId}
                subjectName={selectedSubject?.subjectName}
                rankFilter={selectedRank}
                lookAheadMonths={lookAheadMonths}
                onAddToBuilder={handleAddToBuilder}
              />
            </Paper>
          )}

          {/* Tab 3: Class Builder */}
          {tab === 3 && (
            <Paper elevation={0} sx={{ overflowX: 'auto' }}>
              <ClassBuilder
                subjectId={selectedSubjectId}
                subjectName={selectedSubject?.subjectName}
                targetDate={targetDate}
                accumulatedEmployees={accumulatedEmployees}
                onRemoveEmployee={handleRemoveEmployee}
                suggestedGroups={suggestedGroups}
                loading={suggestLoading}
              />
            </Paper>
          )}
        
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>

      <MatrixFilterDialog
        open={filterOpen}
        filters={filters}
        onApply={setFilters}
        onClose={() => setFilterOpen(false)}
      />
    </Box>
  );
};

export default TrainingPlanner;
